/**
 * Global classes for tiered pricing and quantity management
 * Handles dynamic pricing calculations, quantity inputs, and discount badges
 * Supports multi-tier pricing system with currency formatting and localization
 */
class tieredPricing extends HTMLElement {
  constructor() {
    super();

    // Window values
    const {
      locale,
      money_format,
      tiers,
    } = window.tieredPricing;

    const value = `; ${document.cookie}`;
    const parts = value.split(`; cart_currency=`);
    const cart_currency = parts.length === 2 ? parts.pop().split(';').shift() : 'EUR';
    
    const currenciessWithoutCents = "SEK HUF NOK ISK";
    const currenciesWithCommaCentSeperator = "SEK EUR CZK DKK NOK PLN RON";
    const currenciesWithPrefix = "€ $ ¥ £ EUR CHF";
    this.currencyPrefix = currenciesWithPrefix.includes(cart_currency) ? true : false;
    this.withComma = currenciesWithCommaCentSeperator.includes(cart_currency) ? true : false;
    this.withoutCents = currenciessWithoutCents.includes(cart_currency) ? true : false;

    this.locale = locale;
    this.moneyFormat = money_format.replace('{{amount_with_comma_separator}}', '').replace('{{amount}}', '');
    this.tiers = tiers;

    // Data attributes
    this.preventDiscount = (this.dataset.preventDiscount === 'true');
  }

  /**
   * Format price
   * @param { Integer } price
   * @returns local formatted price with 2 decimals
   */
  formatPrice(price) {
    const formatter = new Intl.NumberFormat(this.locale, {
      minimumFractionDigits: this.withoutCents ? 0 : 2,
      maximumFractionDigits: this.withoutCents ? 0 : 2,
    });

    return formatter.format(price / 100);
  }
}


/**
 * Quantity input
 */
 class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );

    this.product_form = this.querySelector(".product__form");

  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.currentTarget.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }

    // Handle tiered pricing
    if (this.product_form) {
      this.product_form.handlePriceAndBadge(this.input.value);
    }

  }
}

if (!customElements.get("quantity-input")) {
  customElements.define("quantity-input", QuantityInput);
}


/**
 * Tiered pricing price
 */
class tieredPricingPrice extends tieredPricing {
  constructor() {
    super();

    this.price = this.dataset.priceRegular;
    this.priceSale = this.querySelector('[data-tiered-price-sale]');
    this.priceRegular = this.querySelector('[data-tiered-price-regular]');
    this.priceRegularMoney = this.dataset.priceRegularMoney;



    this.tiers.forEach(({
      tiered_pricing_threshold,
      tiered_pricing_discount
    }) => this.handlePrice(false, tiered_pricing_threshold, tiered_pricing_discount));
  }

  handlePrice(value, threshold, discount) {
    // If discount is prevented, do nothing
    if (this.preventDiscount) return

    const { tiered_pricing_item_count } = window.tieredPricing;
    let inputValue = value ? value : tiered_pricing_item_count;

    // If window has more then 1 item and value is true, add the value to the window count - 1
    inputValue = (tiered_pricing_item_count > 1 && value) ? (tiered_pricing_item_count + (value - 1)) : inputValue;

    if (inputValue >= threshold) {
      const discountMultiplier = (100 - discount) / 100;

      const salePrice = this.formatPrice((this.price * discountMultiplier));

      // Set the right prices
      let htmlSalePrice = this.currencyPrefix ? `${this.moneyFormat}${salePrice}`.replace('{{amount_no_decimals}}', '') : `${salePrice}${this.moneyFormat}`.replace('{{amount_no_decimals}}', '');
      if (this.withComma) {
        htmlSalePrice = htmlSalePrice.replace('.', ',');
      }
      this.priceSale.innerHTML = htmlSalePrice;
      this.priceRegular.innerHTML = this.priceRegularMoney;
      
    // If value is lower than the lowest threshold, reset the price
    } else if (inputValue < this.tiers[0].tiered_pricing_threshold) {
      this.priceSale.innerHTML = this.priceRegularMoney;
      this.priceRegular.innerHTML = '';
    }
  }
}

if (!customElements.get('tiered-pricing-price')) {
  customElements.define('tiered-pricing-price', tieredPricingPrice);
}


/**
 * Tiered pricing badge
 */
 class tieredPricingBadge extends tieredPricing {
  constructor() {
    super();

    this.badge = this.querySelector('.product__badge--tiered-sale');
    this.badgeDiscountTypeName = this.dataset.discountTypeName;
    this.badgeDiscountTypeNameShort = this.dataset.discountTypeNameShort;

    // DOM values
    this.tiers.forEach(({tiered_pricing_threshold, tiered_pricing_discount}) => {
      if (this.badge) {
        this.handleBadge(false, tiered_pricing_threshold, tiered_pricing_discount)
      }
    });
  }

  handleBadge(value, threshold, discount) {
    // If discount is prevented, do nothing
    if (this.preventDiscount) return

    const { tiered_pricing_item_count } = window.tieredPricing
    let inputValue = value ? value : tiered_pricing_item_count;

    // If window has more than 1 item and value is true, add the value to the window count - 1
    inputValue = (tiered_pricing_item_count > 1 && value) ? (tiered_pricing_item_count + (value - 1)) : inputValue


    if (inputValue >= threshold) {
      this.classList.remove('product__badges--hidden');
      if (this.badge) { 
        this.badge.classList.remove('product__badge--hidden');
        this.badge.innerHTML = `
        <span class="product__badge--tiered-sale-label shortest">-</span>${discount * 1}%
        <span class="product__badge--tiered-sale-label long">${this.badgeDiscountTypeName}</span>
        <span class="product__badge--tiered-sale-label short">${this.badgeDiscountTypeNameShort}</span>
        `;
      }

    // If value is lower than the lowest threshold, reset the badge
    } else if (inputValue < this.tiers[0].tiered_pricing_threshold) {
      this.classList.add('product__badges--hidden');
      if (this.badge) {
        this.badge.classList.add('product__badge--hidden');
        this.badge.innerHTML = `
        <span class="product__badge--tiered-sale-label shortest">-</span>0%
        <span class="product__badge--tiered-sale-label long">${this.badgeDiscountTypeName}</span>
        <span class="product__badge--tiered-sale-label short">${this.badgeDiscountTypeNameShort}</span>
        `;
      }
    }
  }
}

if (!customElements.get('tiered-pricing-badge')) {
  customElements.define('tiered-pricing-badge', tieredPricingBadge);
}

/**
 * Tiered pricing price
 */
class tieredPricingUnitPrice extends tieredPricing {
  constructor() {
    super();

    this.unitPrice = parseFloat(this.dataset.unitPriceRegular);
    this.unitPriceRegularMoney = this.dataset.unitPriceRegularMoney;

    this.tiers.forEach(({ tiered_pricing_threshold, tiered_pricing_discount }) => {
      this.handleUnitPrice(false, tiered_pricing_threshold, tiered_pricing_discount);
    });
  }

  handleUnitPrice(value, threshold, discount) {
    if (!this.unitPrice) return;
    if (this.preventDiscount) return;

    const { tiered_pricing_item_count } = window.tieredPricing;
    let inputValue = value ? value : tiered_pricing_item_count;

    inputValue = (tiered_pricing_item_count > 1 && value) ? (tiered_pricing_item_count + (value - 1)) : inputValue;

    if (inputValue >= threshold) {
      const discountMultiplier = (100 - discount) / 100;
      const saleUnitPrice = this.formatPrice(this.unitPrice * discountMultiplier);

      const htmlSaleUnitPrice = this.currencyPrefix ? `${this.moneyFormat}${saleUnitPrice}`.replace('{{amount_no_decimals}}', '') : `${saleUnitPrice}${this.moneyFormat}`.replace('{{amount_no_decimals}}', '');


      const unitPriceElement = this.querySelector('[data-unit-price]');
      if (unitPriceElement) {
        unitPriceElement.innerHTML = htmlSaleUnitPrice;
      }
    } else if (inputValue < this.tiers[0].tiered_pricing_threshold) {
      const unitPriceElement = this.querySelector('[data-unit-price]');
      if (unitPriceElement) {
        unitPriceElement.innerHTML = this.unitPriceRegularMoney;
      }
    }
  }
}

if (!customElements.get('tiered-pricing-unit-price')) {
  customElements.define('tiered-pricing-unit-price', tieredPricingUnitPrice);
}